import {
  createContext,
  useState,
  ReactNode,
  useContext
} from "react";
import { apiV2 } from "../services/api";

interface LogsRequest {
  page?: number;
  pageSize?: number;
  createdAtFrom?: string;
  createdAtTo?: string;
  isStocked?: boolean;
  userId?: string;
  orderId?: string;
}

interface Log {
  id_historic: string;
  e_mail: string;
  user_action: string;
  created_at: string;
  fk_users_id_user: string;
  isstocked?: boolean;
  notes?: string | null;
  is_finished_product_order_stock?: boolean;
}

interface LogsProvidersProps {
  children: ReactNode;
}

interface ResponsePropsWithData {
  success: boolean;
  message: string;
  data: any;
}

interface LogsContextData {
  logs: Log[];
  loading: boolean;
  getFilteredLogs: (request: LogsRequest) => Promise<ResponsePropsWithData>;
}

const LogsContext = createContext<LogsContextData>({} as LogsContextData);

function isEmptyFilter(value: unknown) {
  return value === undefined || value === null || value === "" || value === "''";
}

function removeEmptyFilters(request: LogsRequest) {
  return Object.entries(request).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (!isEmptyFilter(value)) acc[key] = value;
    return acc;
  }, {});
}

function isTechnicalMessage(message: unknown) {
  if (typeof message !== "string") return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownApiMessage(message: unknown) {
  if (typeof message !== "string") return undefined;

  const normalizedMessage = message.trim();

  if (/^validation failed\.?$/i.test(normalizedMessage)) {
    return "Não foi possível validar os filtros enviados. Revise as datas e tente novamente.";
  }

  if (/expected a valid date in yyyy-mm-dd format/i.test(normalizedMessage)) {
    return "Informe as datas no formato AAAA-MM-DD.";
  }

  if (/number must be (greater than or equal to|less than or equal to)/i.test(normalizedMessage) ||
    /(page|pagesize).*(greater than|less than|min|max|integer|number)/i.test(normalizedMessage)) {
    return "Informe uma página e uma quantidade por página válidas.";
  }

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return "Você não tem permissão para realizar esta ação.";
  }

  if (/^unauthorized\.?$/i.test(normalizedMessage)) {
    return "Sessão expirada ou não autorizada. Faça login novamente.";
  }

  return undefined;
}

function getApiMessage(response: any) {
  return response?.data?.message || response?.message || response?.data?.errors?.message || response?.data?.errors?.toString?.();
}

export function LogsProvider({ children }: LogsProvidersProps) {
  const [logs] = useState<Log[]>([]);
  const [loading] = useState<boolean>(true);

  async function getFilteredLogs(request: LogsRequest) {
    const response: any = await apiV2.get("/audit-logs", {
      params: removeEmptyFilters(request)
    });
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    if (success) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }

    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownApiMessage(apiMessage);

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? "Erro ao consultar histórico." : apiMessage),
      data: []
    };
  }

  return (
    <LogsContext.Provider value={{ logs, loading, getFilteredLogs }}>
      {children}
    </LogsContext.Provider>
  );
}

export function UseLogs() {
  const context = useContext(LogsContext);
  return context;
}
